import { Toolbar, Typography, IconButton, InputLabel, Grid, Tooltip } from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import React, {useState, useRef} from 'react';
import MuiDrawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import List from '@mui/material/List';
import Badge from '@mui/material/Badge';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { GlobalListItems, MainListItems, SecondaryListItems } from './listItems';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';
import { useContext } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import { useUnreadCount } from '../../UnreadCountContext';
import { DeviceManagement, setNumberOfAvailableKiosks } from '../Settings/DeviceManagement';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import theme from '../../theme/theme';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';


function Navigation({ children, title, onNavigate }) {
    
    const drawerWidth = 280;
    const mobileBreakpoint = 600;
    const isMobile = window.innerWidth <= mobileBreakpoint;

 
    const [open, setOpen] = React.useState(!isMobile);
    const [userRole, setUserRole] = React.useState('');
    const { user } = useContext(AuthContext);
    const { setUser } = useContext(AuthContext);
    const [kiosks, setKiosks] = useState([]);
    const { globalTenantId, setGlobalTenantId } = useContext(AuthContext);  
    const [selectedKiosk, setSelectedKiosk] = useState(globalTenantId);
    const [numberOfAvailableKiosks, setNumberOfAvailableKiosks] = useState(1); // Example number, populate from the API if needed
    const [kioskTypes, setKioskTypes] = useState([]);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const { unreadCounts, setUnreadCounts, refreshUnreadCounts } = useUnreadCount();
    const listRef = useRef(null);
    const indicatorRef = useRef(null);
    const location = useLocation();

    React.useEffect(() => {
      if (user && user.role) {
        setUserRole(user.role);
      }
    }, [user]);

    React.useEffect(() => {
      refreshKiosks();
    }, [selectedKiosk]);  // Dependency array now includes selectedKiosk
  
    const fetchUnreadCounts = async () => {
      let counts = {};
      for (const kiosk of kiosks) {
        try {
          const response = await axios.get('/api/videoMessage-unread', { params: { tenantId: kiosk.id } });
          counts[kiosk.id] = response.data.count;
        } catch (error) {
          console.error(`Error fetching unread count for kiosk ${kiosk.id}:`, error);
          counts[kiosk.id] = 0; // or handle the error differently
        }
      }
      setUnreadCounts(counts);
    };

    // Assign the fetching logic to the context's method
    React.useEffect(() => {
      refreshUnreadCounts.current = fetchUnreadCounts;
    }, [fetchUnreadCounts]);
    
    React.useEffect(() => {
      if (kiosks.length === 0) return;
      fetchUnreadCounts();
    }, [kiosks, globalTenantId]);
    
    
    const refreshKiosks = () => {
      // partners have no devices of their own; the server answers this with an error
      if (user?.role === 'PARTNER') return;
      axios.get('/api/kiosks?adminOnly=true')
        .then(response => {
          if (response.data.length === 0) {
            // no access to kiosks - log out
            handleSignOut(true);
            return;
          }
          setKiosks(response.data);
          //set numberofavailablekiosks
          setNumberOfAvailableKiosks(response.data[0].Customer.NumberOfAllowedKiosks);
          
          // Keep the selected kiosk if this user can still see it, otherwise fall back to the 'current' one
          if (!response.data.some(kiosk => kiosk.id === globalTenantId)) {
            const currentKiosk = response.data.find(kiosk => kiosk.current === true) || response.data[0];
            setSelectedKiosk(currentKiosk.id);
            setGlobalTenantId(currentKiosk.id);
          }


        })
        .catch(error => {
          console.error('Error fetching kiosks:', error);
          // Additional error handling logic here
        });
         // populate kiosk types dropdown
      axios.get('/api/kiosk-types')
      .then(response => {
        if (response.data.length === 0) {
          // no access to kiosks - log out
          throw new Error('No kiosk types available');
        }
        // populate dropdown
        setKioskTypes(response.data);
                
      })
      .catch(error => {
        console.error('Error fetching kiosk types:', error);
        // Additional error handling logic here
      });
    }

     const handleKioskChange = (event) => {
        // Check for navigation before changing kiosk
        if (onNavigate && !onNavigate()) {
            return;
        }
        
        const selectedKioskId = event.target.value;
        setSelectedKiosk(selectedKioskId);
        setGlobalTenantId(selectedKioskId);  
    };    

    const toggleDrawer = () => {
      const newState = !open;
      setOpen(newState);
      localStorage.setItem("drawerOpen", JSON.stringify(newState));
  };
  
  React.useEffect(() => {
    const storedState = localStorage.getItem('drawerOpen');

    if (storedState !== null && !isMobile) {
      setOpen(storedState === "true");
    }
  }, []); 
  

    const navigate = useNavigate();

    const handleNavigationWithCheck = (navigateFunction, targetPath) => {
        // Don't check navigation if we're going to the current page
        const currentPath = window.location.pathname;
        if (currentPath === targetPath) {
            return;
        }

        navigateFunction();
    };

    const handleGoToLogs = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/logs');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/logs');
    };    

    const handleOnGoToNumberPlates = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/number-plates');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/number-plates');
    };

    const handleGoToPins = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/pins');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/pins');
    };     
    
    const handleGoToFaceProfiles = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/face-profiles');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/face-profiles');
    };     
    
    const handleGoToSettings = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/settings');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/settings');
    }; 

    const handleGoToMessages = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/video-messages');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/video-messages');
    }; 

    const handleGoToSnapshots = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/snapshots');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/snapshots');
    };     

    const handleGoToMovementVideos = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/movement-videos');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/movement-videos');
    }; 

    const handleGoToUsers = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/users');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/users');
    }; 

    const handleGoToResponderAliases = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/responder-aliases');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/responder-aliases');
    }; 

    const handleGoToHome = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/');
    };

    const handleUpgrade = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/upgrade');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/upgrade');
    };

    const handleGoToQrCodeKeys = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/qr-code-keys');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/qr-code-keys');
    };

    const handleGoToDevelopers = () => {
        handleNavigationWithCheck(() => {
            navigate('/client-admin/developers');
            if (isMobile) {
                setOpen(false);
            }
        }, '/client-admin/developers');
    };

    const handleGoToPartnerHome = () => {
        handleNavigationWithCheck(() => {
            navigate('/partner-admin/');
            if (isMobile) {
                setOpen(false);
            }
        }, '/partner-admin/');
    };

    const handleGoToPartnerCustomers = () => {
        handleNavigationWithCheck(() => {
            navigate('/partner-admin/customers');
            if (isMobile) {
                setOpen(false);
            }
        }, '/partner-admin/customers');
    };

    const handleGoToReferredPartners = () => {
        handleNavigationWithCheck(() => {
            navigate('/partner-admin/referred-partners');
            if (isMobile) {
                setOpen(false);
            }
        }, '/partner-admin/referred-partners');
    };

    const handleGoToPartnerLogo = () => {
        handleNavigationWithCheck(() => {
            navigate('/partner-admin/logo');
            if (isMobile) {
                setOpen(false);
            }
        }, '/partner-admin/logo');
    };

    const handleSignOut = async (redirectToHelpPage = false) => {
        try {
            // Make a GET request to the logout route in the backend
            const response = await fetch('/api/logout');

            if (response.ok) {
                // clear the session state
                //sessionStorage.removeItem('user');
                setUser(null);
                setGlobalTenantId(null);

                // If logout was successful, redirect to the login page
                if (redirectToHelpPage === true) {
                  navigate('/help');
                } else {
                  navigate('/login');
                }
            } else {
                // Handle any errors that might occur during logout
                console.error('Failed to log out');
            }
        } catch (error) {
            console.error('An error occurred:', error);
        }
    };

    const handlePreviewKiosk = () => {
      if (!globalTenantId) {
        console.log('No tenantid in context');
        return;
      }

      // show a popup with the kiosk in it
      let kioskUrl;
      axios.get('/api/setting?name=KIOSKURL')
      .then(result => {
          const data = result.data;
          kioskUrl = data.value + '?tenantId=' + globalTenantId;
          window.open(kioskUrl, 'kiosk', 'width=400,height=600');
        })
      .catch(error => console.error('Error fetching data:', error))

    };
    const AppBar = styled(MuiAppBar, {
        shouldForwardProp: (prop) => prop !== 'open',
      })(({ theme, open }) => ({
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        backgroundImage: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
        boxShadow: '0 10px 30px rgba(2, 6, 23, 0.10)',
        backdropFilter: 'saturate(140%) blur(6px)',
        WebkitBackdropFilter: 'saturate(140%) blur(6px)',
        ...(open && {
          marginLeft: drawerWidth,
          width: `calc(100% - ${drawerWidth}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }),
      }));
      const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
        ({ theme, open }) => ({
          flexShrink: 0,
          overflowX: 'clip',
          width: drawerWidth,
          ...(!open && {
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
              width: theme.spacing(9),
            },
          }),
          '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            overflowX: 'clip',
            touchAction: 'pan-y',
            backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #f5f7fb 100%)',
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'saturate(140%) blur(6px)',
            WebkitBackdropFilter: 'saturate(140%) blur(6px)',
            boxShadow: '0 0 24px rgba(2, 6, 23, 0.06)',
            borderRight: '1px solid rgba(2, 6, 23, 0.06)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              width: theme.spacing(7),
              [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9),
              },
            }),
            '& .MuiList-root': {
              overflowX: 'clip',
              overflowY: 'auto',
              padding: theme.spacing(2, 1),
              '& .MuiListItemButton-root': {
                borderRadius: theme.shape.borderRadius,
                margin: theme.spacing(0.5, 0),
                padding: open ? theme.spacing(1, 2) : theme.spacing(1),
                justifyContent: open ? 'initial' : 'center',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(58, 123, 213, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(58, 123, 213, 0.15)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: open ? 40 : 0,
                marginRight: open ? 'auto' : 'auto',
                justifyContent: 'center',
              },
              '& .MuiListItemText-root': {
                opacity: open ? 1 : 0,
                minWidth: 0,
                overflow: 'hidden',
                ...(!open && {
                  width: 0,
                  flex: 'none',
                  margin: 0,
                }),
                transition: theme.transitions.create(['opacity', 'margin'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
              '& .MuiListSubheader-root': {
                overflow: 'hidden',
                ...(!open && {
                  height: 0,
                  minHeight: 0,
                  padding: 0,
                  lineHeight: 0,
                  visibility: 'hidden',
                }),
              },
            },
          },
        }),
      );
      function Copyright(props) {
        return (
          <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Copyright © '}
            <Link color="inherit" href="https://entrinsic.io/">
              Entrinsic Connect
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
          </Typography>
        );
      }

      const openSettingsDialog = () => {
        // Only check for unsaved changes if we're on the settings page
        if (window.location.pathname === '/client-admin/settings' && !onNavigate?.(true)) {
            alert('Please save or discard your changes before managing devices.');
            return;
        }
        setSettingsDialogOpen(true);
    }
      const handleSettingsClose = () => {
        setSettingsDialogOpen(false);
      }
     
      const anyUnreads = Object.values(unreadCounts).some(count => count > 0);

      

    const checkScrollability = (element) => {
        // Only return true if there's actually content that needs scrolling
        return element.scrollHeight > element.clientHeight;
    };

    const handleScroll = (e) => {
        const element = e.target;
        const reachedBottom = Math.abs(
            element.scrollHeight - element.scrollTop - element.clientHeight
        ) < 1;
        const needsScrolling = checkScrollability(element);
        
        // Update the indicator visibility directly through the DOM
        if (indicatorRef.current) {
            indicatorRef.current.style.opacity = needsScrolling && !reachedBottom ? '1' : '0';
            indicatorRef.current.style.visibility = needsScrolling && !reachedBottom ? 'visible' : 'hidden';
        }
    };

    React.useEffect(() => {
        const listElement = listRef.current;
        if (!listElement) return;

        // Initial check
        const needsScrolling = checkScrollability(listElement);
        if (indicatorRef.current) {
            indicatorRef.current.style.opacity = needsScrolling ? '1' : '0';
            indicatorRef.current.style.visibility = needsScrolling ? 'visible' : 'hidden';
        }
    }, [location.pathname, open, userRole]);

    return (
        <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }} data-intro='Welcome to Entrinsic Connect. Please follow this short guided tour of the web portal. Feel free to close the window to come back to this next time you visit the Dashboard. Alternatively select "Dont show this again" to hide this tour.'>
          <CssBaseline />
        <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              pr: '24px',
              minHeight: 64,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: '36px',
                ...(open && { display: 'none' }),
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                padding: '8px',
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              sx={{ 
                flexGrow: 1,
                fontWeight: 700,
                letterSpacing: '0.3px',
                textShadow: '0 1px 0 rgba(0,0,0,0.08)'
              }}
            >
              {title || "Dashboard"}
            </Typography>
            { user.partnerId && !user.is_partner_user && (
              <Tooltip title="To add new devices please contact your partner">
            <Typography variant='body2' style={{marginRight: 20, color: '#cccccc'}}>Your system is managed by {user.partnerName}</Typography>
            </Tooltip>
            )}
             {userRole.startsWith('CLIENT_') && (
              <>
              { !isMobile && (
              <Typography 
                variant='body2' 
                sx={{ 
                  marginRight: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 500,
                }}
              >
                Switch device <ArrowRightIcon fontSize='small' style={{verticalAlign: 'middle', marginLeft: 4}} />
              </Typography>
              )}

              <Box data-intro='This is the device selector. The dashboard, and most of the menus on the left, are specific to the device you have selected here, assuming you have more than one device.'>
          <Badge
              color="primary"
              badgeContent=''
              invisible={!anyUnreads}
              sx={{
                '.MuiBadge-badge': {
                  height: '12px',
                  minWidth: '12px',
                  fontSize: '0.4rem',
                  borderRadius: '50%',
                  backgroundColor: 'red',
                  top: '10px',
                }
              }}
            >
              <Select
                value={selectedKiosk}
                onChange={handleKioskChange}
                label="Kiosk"
                style={{ color: '#f0f0f0' }} 
                

              >
                {kiosks.map((kiosk) => {
                return  (
                    
                        <MenuItem value={kiosk.id}>
                          {kiosk.OrganisationName}
                          {unreadCounts[kiosk.id] > 0 && (
                            <span style={{
                                marginLeft: '10px',
                                backgroundColor: 'red',
                                borderRadius: '50%',
                                color: 'white',
                                display: 'inline-block',
                                padding: '0 6px',
                                fontSize: '0.75rem',
                                lineHeight: '1.5',
                                verticalAlign: 'middle'
                            }}>
                                {unreadCounts[kiosk.id]}
                            </span>
                          )}
                        </MenuItem>
                    
                    )})}
              </Select>
            </Badge>
            </Box>
            </>
            )}   
            {userRole.startsWith('CLIENT_ADMIN') && (!user.partnerId || user.is_partner_user) && (     
            <IconButton 
                color="inherit" 
                onClick={openSettingsDialog}
                data-intro='Use this button to show the Manage Devices screen. This is where you can add more devices.'
            >
                <SettingsIcon />
            </IconButton>)}
            <Tooltip title={user.email || 'User'}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                ml: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </Typography>
              </Box>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              px: open ? 2 : 0,
              minHeight: 64,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: open ? 'flex' : 'none', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
              <img 
                src="/Entrinsic_Logo.png" 
                alt="Company Logo" 
                style={{ 
                  height: '40px',
                  width: 'auto', 
                  marginRight: '8px',
                  transition: 'opacity 0.2s ease',
                  opacity: open ? 1 : 0,
                }} 
              />
            </Box>
            <IconButton 
              onClick={toggleDrawer}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
                borderRadius: 2
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider sx={{ margin: '8px 0' }} />
          <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden', minWidth: 0 }}>
            <List 
                component="nav" 
                ref={listRef}
                onScroll={handleScroll}
                sx={{ 
                    overflowX: 'clip',
                    overflowY: 'auto',
                    touchAction: 'pan-y',
                    height: '100%', 
                    pb: 6,
                    scrollBehavior: 'smooth',
                    msOverflowStyle: 'none',  // Hide scrollbar in IE/Edge
                    scrollbarWidth: 'none',   // Hide scrollbar in Firefox
                    '&::-webkit-scrollbar': { // Hide scrollbar by default
                        display: 'none'
                    },
                    '&:hover': {
                        msOverflowStyle: 'auto',  // Show scrollbar on hover in IE/Edge
                        scrollbarWidth: 'thin',   // Show scrollbar on hover in Firefox
                        '&::-webkit-scrollbar': {
                            width: '0.4em',
                            display: 'block'
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,.1)',
                            borderRadius: '4px'
                        }
                    }
                }}
            >
              <div data-intro='Most of the menu options here are specific to the device selected in the top right.'>
                <MainListItems 
                              userRole={userRole}
                              currentPath={location.pathname}
                              onGoToLogs={handleGoToLogs}
                              onGoToMovementVideos={handleGoToMovementVideos}
                              onGoToDashboard={handleGoToHome}
                              onGoToNumberPlates={handleOnGoToNumberPlates}
                              onGoToPins={handleGoToPins} 
                              onGoToFaceProfiles={handleGoToFaceProfiles}
                              onGoToMessages={handleGoToMessages}
                              onGoToSnapshots={handleGoToSnapshots}
                              onGoToQrCodeKeys={handleGoToQrCodeKeys}
                              onGoToPartnerHome={handleGoToPartnerHome}
                              onGoToPartnerCustomers={handleGoToPartnerCustomers}
                              onGoToReferredPartners={handleGoToReferredPartners}
                              onGoToPartnerLogo={handleGoToPartnerLogo}
                              onGoToCustomers={() => navigate('/admin/customers')}
                              unreadCount={unreadCounts[selectedKiosk] || 0}
                              onNavigate={onNavigate}
                              />
                {userRole.startsWith('CLIENT_') && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <SecondaryListItems 
                                userRole={userRole}
                                currentPath={location.pathname}
                                user={user}
                                onPreviewKiosk={handlePreviewKiosk}
                                onGoToSettings={handleGoToSettings}
                                unreadCount={unreadCounts[selectedKiosk] || 0}
                                onNavigate={onNavigate}
                                />
                  </>
                )}
              </div>
              <Divider sx={{ my: 1 }} />
              <div data-intro='These menu options apply to all devices.'>
              <GlobalListItems 
                              userRole={userRole}
                              currentPath={location.pathname}
                              user={user}
                              onSignOut={handleSignOut}
                              onGoToUsers={handleGoToUsers}
                              onGoToResponderAliases={handleGoToResponderAliases}
                              onUpgrade={handleUpgrade}
                              unreadCount={unreadCounts[selectedKiosk] || 0}
                              onNavigate={onNavigate}
                              onGoToDevelopers={handleGoToDevelopers}
                              />
              </div>
            </List>
            
            
              <Box
                ref={indicatorRef}
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    pb: 1,
                    background: 'linear-gradient(transparent, rgba(255,255,255,0.9) 50%)',
                    pointerEvents: 'none',
                    zIndex: 1,
                    height: '40px',
                    transition: 'opacity 0.2s, visibility 0.2s',
                }}
              >
                    <KeyboardArrowDownIcon 
                        sx={{ 
                            color: 'primary.main',
                        }}
                    />
              </Box>
            
          
          </Box>
        </Drawer>
       
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        ><Toolbar />
          <Container 
            maxWidth="lg" 
            sx={{ 
              mt: 4, 
              mb: 4, 
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box 
              sx={{ 
                flexGrow: 1,
                animation: 'fadeIn 0.3s ease-in-out',
                '@keyframes fadeIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(10px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {children}
            </Box>
            <Copyright sx={{ pt: 4 }} />
          </Container>
          </Box>
        </Box>
        
        {userRole.startsWith('CLIENT_ADMIN') && (   
        <DeviceManagement kiosks={kiosks}
                          kioskTypes={kioskTypes}
                          numberOfAvailableKiosks={numberOfAvailableKiosks}
                          settingsDialogOpen={settingsDialogOpen}
                          setSettingsDialogOpen={setSettingsDialogOpen}
                          setGlobalTenantId={setGlobalTenantId}
                          setSelectedKiosk={setSelectedKiosk}
                          refreshKiosks={refreshKiosks}
                          handleSettingsClose={handleSettingsClose}
                          />
        )}

      
    </ThemeProvider>
    );
}

export default Navigation;
